import { environment } from '@env/environment';

declare const google: typeof globalThis.google;

export type GoogleMapsLibraryName = 'core' | 'maps' | 'marker' | 'places' | 'geocoding';

declare global {
  interface Window {
    /** Set by the early index.html interceptor (and mirrored here as a fallback). */
    __gabaliFontDisplaySwapPatched?: boolean;
  }
}

/**
 * Fallback if index.html interceptor did not run (tests / non-standard hosts).
 * Primary patch lives in `src/index.html` so it runs before Angular + Maps.
 */
function ensureWebFontDisplaySwap(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  if (window.__gabaliFontDisplaySwapPatched) {
    return;
  }
  window.__gabaliFontDisplaySwapPatched = true;

  const OriginalFontFace = window.FontFace;
  window.FontFace = class extends OriginalFontFace {
    constructor(
      family: string,
      source: string | BufferSource,
      descriptors?: FontFaceDescriptors,
    ) {
      const display = descriptors?.display;
      super(family, source, {
        ...descriptors,
        display: display && display !== 'auto' ? display : 'swap',
      });
    }
  };
}

function waitForMapsImportLibrary(timeoutMs = 15000): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is unavailable during SSR'));
  }

  return new Promise((resolve, reject) => {
    const started = Date.now();

    const check = () => {
      if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function') {
        resolve();
        return;
      }

      if (Date.now() - started > timeoutMs) {
        reject(new Error('Timed out waiting for google.maps.importLibrary'));
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });
}

function loadGoogleMapsScript(): Promise<void> {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is unavailable during SSR'));
  }

  ensureWebFontDisplaySwap();

  if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function') {
    return Promise.resolve();
  }

  const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  if (existing) {
    return waitForMapsImportLibrary();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.MAPS_KEY}&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    script.onload = () => {
      waitForMapsImportLibrary().then(resolve).catch(reject);
    };
    document.head.appendChild(script);
  });
}

const libraryPromises = new Map<GoogleMapsLibraryName, Promise<unknown>>();

export async function importGoogleMapsLibrary(name: GoogleMapsLibraryName): Promise<unknown> {
  await loadGoogleMapsScript();

  const existing = libraryPromises.get(name);
  if (existing) {
    return existing;
  }

  const promise = google.maps.importLibrary(name).catch((error) => {
    libraryPromises.delete(name);
    throw error;
  });
  libraryPromises.set(name, promise);
  return promise;
}

/** Interactive map rendering (map tiles, markers, clustering). */
export function loadGoogleMapsMapLibraries(): Promise<void> {
  return Promise.all([
    importGoogleMapsLibrary('maps'),
    importGoogleMapsLibrary('marker'),
  ]).then(() => undefined);
}

/** Place Autocomplete search widgets. */
export function loadGoogleMapsPlacesLibrary(): Promise<void> {
  return importGoogleMapsLibrary('places').then(() => undefined);
}

/** Client-side geocoding fallback for address search. */
export function loadGoogleMapsGeocodingLibrary(): Promise<void> {
  return importGoogleMapsLibrary('geocoding').then(() => undefined);
}
